import {
  Stack,
  Loader,
  Box,
  FeedbackBlock,
  useIsMobileLayout,
  Body,
  Space,
  Button,
} from "@dbn/omnichannel-design-system-g4p";
import { RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { OfferChooseCard } from "./OfferChooseCard";
import * as standardLoanApi from "../api";
import { useCommand } from "../../useAPI";
import { pipe } from "fp-ts/function";
import { array, boolean, option, taskEither } from "fp-ts";
import { IO } from "fp-ts/IO";
import { Reader } from "fp-ts/Reader";
import * as classes from "./OfferChoose.css";
import cx from "classnames";
import { useScrollTopOnChange } from "../../Common/useScrollTopOnChange";
import * as apis from "./api";
import { GenericLoanOffer } from "../api";
import { useEffect, useState } from "react";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";

type Props = {
  onSelectOffer: Reader<standardLoanApi.GenericLoanResponseOutput, unknown>;
  onBack: IO<unknown>;
  offerState: Exclude<apis.CheckOfferStatus, "PENDING">;
};

type ChildProps = {
  onSelectOffer: Reader<standardLoanApi.GenericLoanResponseOutput, unknown>;
  isMobileLayout: boolean;
};

export function SelectedOffer({ onSelectOffer }: ChildProps) {
  const formatMessage = useFormatMessage();
  const getSelectedOffer = useCommand(apis.getSelectedOffer);
  const saveSelectedOffer = useCommand(apis.saveSelectedOffer);

  //@ts-ignore
  useEffect(() => {
    pipe(
      getSelectedOffer(),
      taskEither.chain(offer =>
        pipe(
          saveSelectedOffer(offer),
          taskEither.chain(selectedOffer =>
            taskEither.fromIO(() => onSelectOffer(selectedOffer))
          )
        )
      )
    )();
  }, []);

  return (
    <Box hAlignContent="center" vAlignContent="center">
      <Loader
        ariaLabelMessage={formatMessage(
          "generic.loading.alt" as RuntimeLocaleKey
        )}
      />
    </Box>
  );
}

export function UpsellOffers({ onSelectOffer, isMobileLayout }: ChildProps) {
  const formatMessage = useFormatMessage();
  const getUpsellOffers = useCommand(apis.getUpsellOffers);
  const saveSelectedOffer = useCommand(apis.saveSelectedOffer);
  const [offers, setOffers] = useState<NonEmptyArray<GenericLoanOffer>>();

  //@ts-ignore
  useEffect(() => {
    pipe(
      getUpsellOffers(),
      taskEither.chain(({ offers }) =>
        pipe(
          offers,
          option.fromPredicate(l => l.length === 1),
          option.map(array.head),
          option.flatten,
          option.fold(
            () => taskEither.fromIO(() => setOffers(offers)),
            offer =>
              pipe(
                saveSelectedOffer(offer),
                taskEither.chain(selectedOffer =>
                  taskEither.fromIO(() => onSelectOffer(selectedOffer))
                )
              )
          )
        )
      )
    )();
  }, []);

  if (!offers) {
    return (
      <Box hAlignContent="center" vAlignContent="center">
        <Loader
          ariaLabelMessage={formatMessage(
            "generic.loading.alt" as RuntimeLocaleKey
          )}
        />
      </Box>
    );
  }

  return (
    <>
      <FeedbackBlock
        size="large"
        type="positive"
        heading={formatMessage("StandardLoan.OfferApproved.ChooseOffer.title")}
        subheading={option.some(
          formatMessage("StandardLoan.OfferApproved.ChooseOffer.subtitle")
        )}
        actions={[]}
      />
      <Box hAlignContent={"center"}>
        <Body size={"medium"} weight={"regular"} align={"center"}>
          {formatMessage("StandardLoan.OfferApproved.ChooseOffer.description")}
        </Body>
      </Box>
      <Space units={8} />
      <Stack units={8} hAlignContent="center" column={isMobileLayout}>
        {pipe(
          offers,
          array.map(offer => (
            <OfferChooseCard
              onComplete={onSelectOffer}
              offer={offer}
              expanded={true}
            />
          ))
        )}
      </Stack>
    </>
  );
}

export function OfferChoose(props: Props) {
  const formatMessage = useFormatMessage();
  useScrollTopOnChange("smooth");

  const isMobileLayout = useIsMobileLayout();

  return (
    <Stack column className={cx(isMobileLayout && classes.containerMobile)}>
      {pipe(
        props.offerState === "RECEIVED",
        boolean.fold(
          () => (
            <SelectedOffer
              isMobileLayout={isMobileLayout}
              onSelectOffer={props.onSelectOffer}
            />
          ),
          () => (
            <UpsellOffers
              isMobileLayout={isMobileLayout}
              onSelectOffer={props.onSelectOffer}
            />
          )
        )
      )}
      <Space units={8} />
      <Box grow column={isMobileLayout}>
        <Button
          variant="secondary"
          size="default"
          label={formatMessage(
            "StandardLoan.OfferApproved.ChooseOffer.backButton"
          )}
          action={props.onBack}
          disabled={false}
        />
      </Box>
    </Stack>
  );
}
